<div class="besharp-chat">
  <!-- ── Messages area ─────────────────────────────────────────────────────── -->
  <div class="chat-messages" #messagesContainer [class.is-streaming]="isStreaming">
    @if (isLoadingHistory) {
      <!-- Skeleton placeholders shaped like messages -->
      @for (i of skeletonRows; track i) {
        <div class="chat-message {{ i % 2 === 0 ? 'assistant' : 'user' }}">
          <div class="message-avatar skeleton-avatar">
            <p-skeleton shape="circle" size="2.25rem" />
          </div>
          <div class="message-body">
            <p-skeleton width="90px" height="0.7rem" styleClass="mb-2" />
            <p-skeleton [width]="i % 2 === 0 ? '280px' : '220px'" height="2.5rem" borderRadius="14px" />
          </div>
        </div>
      }
    } @else {
      <!-- Empty state -->
      @if (messages.length === 0) {
        @if (emptyStateTemplate) {
          <ng-container *ngTemplateOutlet="emptyStateTemplate; context: { $implicit: emptyState }"></ng-container>
        } @else {
          <div class="chat-empty">
            <div class="empty-icon-wrapper">
              <i [class]="emptyState.icon"></i>
            </div>
            <h3>{{ emptyState.title }}</h3>
            <p>{{ emptyState.description }}</p>
            @if (emptyState.suggestions && emptyState.suggestions.length > 0) {
              <div class="empty-suggestions">
                <span class="suggestion-title">💡 Example:</span>
                <ul>
                  @for (suggestion of emptyState.suggestions; track $index) {
                    <li>{{ suggestion }}</li>
                  }
                </ul>
              </div>
            }
            <small>Shift + Enter for a new line</small>
          </div>
        }
      }

      <!-- Message list -->
      @for (message of messages; track message.id) {
        <div class="chat-message {{ message.role }}" [class.from-history]="message.isFromHistory">
          <!-- Avatar -->
          <div class="message-avatar">
            @if (message.role === 'user') {
              <i class="pi pi-user"></i>
            } @else {
              <img src="/images/logo.svg" class="logo" />
            }
          </div>

          <!-- Body -->
          <div class="message-body">
            <!-- Header row -->
            <div class="message-header">
              <span class="message-sender">{{ message.role === 'user' ? userLabel : botName }}</span>
              @if (showTimestamps) {
                <span class="message-time">{{ message.timestamp | date: 'shortTime' }}</span>
              }
              @if (message.isFromHistory) {
                <span class="history-badge">History</span>
              }
            </div>

            <!-- Tool steps (shown for assistant messages that have them) -->
            @if (message.toolSteps && message.toolSteps.length > 0) {
              <div class="tool-steps">
                <button
                  class="tool-steps-toggle"
                  type="button"
                  [class.is-open]="isToolStepsExpanded(message.id)"
                  [class.is-running]="message.isStreaming"
                  [attr.aria-expanded]="isToolStepsExpanded(message.id)"
                  [attr.aria-controls]="message.id + '-steps'"
                  (click)="toggleToolSteps(message.id)"
                >
                  <i class="pi pi-brain tool-steps-glyph" aria-hidden="true"></i>
                  <span class="tool-steps-label">Reasoning process</span>
                  @if (message.isStreaming) {
                    <span class="tool-steps-live"><span class="live-dot" aria-hidden="true"></span>running</span>
                  } @else {
                    <span class="tool-steps-count">{{ countSteps(message.toolSteps!) }}</span>
                  }
                  <i
                    class="pi tool-steps-chevron"
                    aria-hidden="true"
                    [class.pi-chevron-right]="!isToolStepsExpanded(message.id)"
                    [class.pi-chevron-down]="isToolStepsExpanded(message.id)"
                  ></i>
                </button>

                @if (isToolStepsExpanded(message.id)) {
                  <div class="tool-steps-list" [attr.id]="message.id + '-steps'">
                    @for (group of getToolGroups(message.toolSteps!); track $index) {
                      <ng-container
                        *ngTemplateOutlet="stepGroup; context: { $implicit: group, msgId: message.id }"
                      ></ng-container>
                    }
                  </div>
                }
              </div>
            }

            <!-- Message content -->
            <div class="message-content">
              @if (message.role === 'user') {
                <div class="user-text" [innerHTML]="message.content | mentions"></div>
              } @else {
                <!-- Typing indicator while streaming with no text yet -->
                @if (message.isStreaming && !message.content) {
                  <div class="typing-indicator"><span></span><span></span><span></span></div>
                } @else {
                  <div class="assistant-text" [innerHTML]="message.content | markdown"></div>
                }
              }
            </div>

            <!-- Feedback (thumbs up/down) — only once the turn's trace_id has arrived  and it is not in history-->
            @if (canGiveFeedback(message)) {
              <div class="feedback-actions">
                <button
                  type="button"
                  class="feedback-btn thumbs-up btn-left"
                  [class.is-active]="feedbackValue(message) === true"
                  [disabled]="isFeedbackPending(message)"
                  title="Good response"
                  (click)="sendFeedback(message, true)"
                >
                  <i class="pi pi-thumbs-up"></i>
                </button>
                <button
                  type="button"
                  class="feedback-btn thumbs-down"
                  [class.is-active]="feedbackValue(message) === false"
                  [disabled]="isFeedbackPending(message)"
                  title="Bad response"
                  (click)="sendFeedback(message, false)"
                >
                  <i class="pi pi-thumbs-down"></i>
                </button>
              </div>
            }
          </div>
        </div>
      }
    }
  </div>

  <!-- ── Error banner ──────────────────────────────────────────────────────── -->
  @if (errorMessage) {
    <div class="chat-error">
      <i class="pi pi-exclamation-triangle"></i>
      <span>{{ errorMessage }}</span>
      <button class="error-dismiss" (click)="dismissError()" type="button">
        <i class="pi pi-times"></i>
      </button>
    </div>
  }

  <!-- ── Input area ────────────────────────────────────────────────────────── -->
  <div class="chat-input-area">
    @if (mentionOpen) {
      <div class="mention-menu" #mentionMenu id="mention-menu" role="listbox" aria-label="Indicators and countries">
        <div class="mention-menu-head">
          <div class="mention-tabs" role="tablist" aria-label="Filter by kind">
            @for (tab of mentionFilters; track tab.value) {
              <button
                type="button"
                role="tab"
                class="mention-tab"
                [class.is-active]="mentionFilter === tab.value"
                [attr.aria-selected]="mentionFilter === tab.value"
                (mousedown)="$event.preventDefault(); setMentionFilter(tab.value)"
              >
                {{ tab.label }}
                <span class="mention-tab-count">{{ mentionCounts[tab.value] }}</span>
              </button>
            }
          </div>
          <span class="mention-tabs-hint" aria-hidden="true">&#8592; &#8594;</span>
        </div>
        @if (mentionMatches.length) {
          @for (reference of mentionMatches; track reference.kind + reference.code; let i = $index) {
            <div
              class="mention-option"
              role="option"
              [id]="'mention-option-' + i"
              [attr.aria-selected]="i === mentionActive"
              [class.is-active]="i === mentionActive"
              (mouseenter)="setMentionActive(i)"
              (mousedown)="$event.preventDefault(); acceptMention(reference)"
            >
              <span class="mention-label">
                <span
                  class="mention-badge"
                  [class.is-country]="reference.kind === 'country'"
                  [class.is-region]="reference.kind === 'region'"
                  >{{ reference.kind }}</span
                >
                {{ reference.label }}
              </span>
              <span class="mention-meta"
                >{{ mentionMetaFor(reference)
                }}<span class="mention-code">{{ reference.kind }}:{{ reference.code }}</span></span
              >
            </div>
          }
        } @else {
          <div class="mention-empty">
            @if (mentionCounts.all) {
              Nothing under <strong>{{ mentionFilterLabel() }}</strong> for “{{ mentionQuery }}” — try another tab.
            } @else {
              No indicator, country or region matches “{{ mentionQuery }}”.
            }
          </div>
        }
      </div>
    }
    @if (quota; as q) {
      @if (q.exhausted) {
        <div class="quota-blocked" role="status">
          <i class="pi pi-hourglass"></i>
          <div>
            <strong>Daily quota reached.</strong>
            You can ask new questions again at {{ q.resetsAt | date: 'HH:mm' }} (in {{ quotaResetIn }}). Your
            conversations, charts and exports remain available. Need more? Contact your administrator.
          </div>
        </div>
      }
    }
    <div class="chat-input-box" [class.is-blocked]="quotaBlocked">
      <div class="chat-input-field">
        <div class="chat-input-mirror" aria-hidden="true" [innerHTML]="composerHighlight"></div>
        <textarea
          #textInput
          class="chat-input"
          [placeholder]="placeholder"
          [(ngModel)]="inputText"
          [disabled]="isStreaming || quotaBlocked"
          (keydown)="onKeydown($event)"
          (input)="onInput($event)"
          (click)="refreshMentionMenu()"
          (blur)="closeMentionMenu()"
          (scroll)="syncMirrorScroll($event)"
          role="combobox"
          aria-autocomplete="list"
          aria-controls="mention-menu"
          [attr.aria-expanded]="mentionOpen"
          [attr.aria-activedescendant]="mentionOpen && mentionMatches.length ? 'mention-option-' + mentionActive : null"
          rows="1"
        ></textarea>
      </div>
      <div class="chat-input-toolbar">
        <div class="model-select-wrapper">
          <i class="pi pi-microchip-ai"></i>
          <select class="model-select" [(ngModel)]="selectedModel" [disabled]="isStreaming">
            @for (option of modelOptions; track option.value) {
              <option [value]="option.value">{{ option.label }}</option>
            }
          </select>
          <i class="pi pi-chevron-down model-chevron"></i>
        </div>
        <!-- Right-hand group: the usage ring sits next to send, so it never moves the model
             selector (the toolbar keeps exactly two children for space-between). -->
        <div class="chat-input-actions">
          @if (quota; as q) {
            @if (!q.unlimited) {
              <button
                type="button"
                class="quota-ring"
                [class.is-warn]="quotaLevel === 'warn'"
                [class.is-critical]="quotaLevel === 'critical'"
                [attr.aria-label]="'Today\'s usage: ' + wholePercent(q.percent) + '%. Show details'"
                [title]="wholePercent(q.percent) + '% of today\'s quota · resets at ' + (q.resetsAt | date: 'HH:mm')"
                (click)="quotaPopover.toggle($event)"
              >
                <svg viewBox="0 0 20 20" aria-hidden="true">
                  <circle class="quota-ring-track" cx="10" cy="10" r="8" />
                  <circle
                    class="quota-ring-arc"
                    cx="10"
                    cy="10"
                    r="8"
                    [attr.stroke-dasharray]="ringCircumference"
                    [attr.stroke-dashoffset]="ringOffset(q.percent)"
                  />
                </svg>
                @if (quotaLevel === 'critical') {
                  <span class="quota-ring-label">{{ wholePercent(q.percent) }}%</span>
                }
              </button>
            }
          }
          <p-popover #quotaPopover styleClass="besharp-quota-popover" (onShow)="onQuotaPopoverShow()">
            @if (quota; as q) {
              <div class="bqp">
                <div class="bqp-head">
                  <span class="bqp-title">Today's usage</span>
                  <span class="bqp-value">{{ wholePercent(q.percent) }}<small>%</small></span>
                </div>
                <div
                  class="bqp-bar"
                  [class.is-warn]="quotaLevel === 'warn'"
                  [class.is-critical]="quotaLevel === 'critical'"
                >
                  <div [style.width.%]="q.percent"></div>
                </div>
                <div class="bqp-meta">
                  <span
                    ><i class="pi pi-refresh" aria-hidden="true"></i>Resets at {{ q.resetsAt | date: 'HH:mm' }} · in
                    {{ quotaResetIn }}</span
                  >
                </div>

                @if (q.history.length) {
                  <div class="bqp-section">Last {{ q.history.length }} days</div>
                  <div class="bqp-week" role="list">
                    @for (d of q.history; track d.day; let last = $last) {
                      <div
                        class="bqp-day"
                        role="listitem"
                        [class.is-today]="last"
                        [title]="
                          (d.day | date: 'EEE d MMM' : 'UTC') +
                          ': ' +
                          wholePercent(d.percent) +
                          '% · ' +
                          d.turns +
                          ' questions'
                        "
                      >
                        <div class="bqp-day-track"><div [style.height.%]="d.percent"></div></div>
                        <span>{{ d.day | date: 'EEEEE' : 'UTC' }}</span>
                      </div>
                    }
                  </div>
                }
                <div class="bqp-foot">Percentages of your daily quota. The limit resets every day at 00:00 UTC.</div>
              </div>
            }
          </p-popover>
          <button
            class="send-button"
            type="button"
            [disabled]="!inputText.trim() || isStreaming || quotaBlocked"
            (click)="sendMessage()"
            title="Send message (Enter)"
          >
            @if (isStreaming) {
              <i class="pi pi-spin pi-spinner"></i>
            } @else {
              <i class="pi pi-send"></i>
            }
          </button>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- One node of a tool payload.
     Recursive: a container renders its own children through the same template, so an
     object of arrays of objects nests without the template knowing the shape in advance.

     Types are told apart without colour — strings carry their quotes, numbers are tabular
     and unquoted, booleans and null are muted. That keeps the panel's one-accent rule
     intact while still making `"42"` distinguishable from `42`. -->
<ng-template #jsonNode let-node let-keyBase="keyBase">
  @if ($any(node).kind === 'object' || $any(node).kind === 'array') {
    <div class="json-node">
      <button
        class="json-row json-row-toggle"
        type="button"
        [attr.aria-expanded]="isJsonExpanded(keyBase + $any(node).path, $any(node))"
        (click)="toggleJson(keyBase + $any(node).path, $any(node))"
      >
        <i
          class="pi json-caret"
          aria-hidden="true"
          [class.pi-chevron-right]="!isJsonExpanded(keyBase + $any(node).path, $any(node))"
          [class.pi-chevron-down]="isJsonExpanded(keyBase + $any(node).path, $any(node))"
        ></i>
        @if ($any(node).key !== null) {
          <span class="json-key">{{ $any(node).key }}</span>
        }
        <!-- The size is the point of a collapsed container: it says whether opening it is
             worth the screen space. -->
        <span class="json-size"
          >{{ $any(node).kind === 'array' ? '[' : '{' }}{{ $any(node).size
          }}{{ $any(node).kind === 'array' ? ']' : '}' }}</span
        >
        @if ($any(node).size === 0) {
          <span class="json-empty">empty</span>
        }
      </button>

      @if (isJsonExpanded(keyBase + $any(node).path, $any(node)) && $any(node).size > 0) {
        <div class="json-children">
          @for (child of $any(node).children; track child.path) {
            <ng-container *ngTemplateOutlet="jsonNode; context: { $implicit: child, keyBase: keyBase }"></ng-container>
          }
        </div>
      }
    </div>
  } @else {
    <div class="json-row json-row-leaf" [attr.title]="jsonTitle($any(node))">
      <span class="json-caret-spacer" aria-hidden="true"></span>
      @if ($any(node).key !== null) {
        <span class="json-key">{{ $any(node).key }}</span>
      }
      <span class="json-value is-{{ $any(node).kind }}">{{ $any(node).value }}</span>
    </div>
  }
</ng-template>

<!-- One reasoning-panel row. Recursive: a sub-agent's group renders the tools it called
       inside its own body, so a direct orchestrator tool call and a sub-agent flow are
       visually distinct rather than a flat sequence. -->
<ng-template #stepGroup let-group let-msgId="msgId">
  @if (group.kind === 'standalone') {
    <!-- agent_thinking / thinking / sub_agent_thinking: plain row -->
    <div class="tool-step {{ $any(group).step.type }}">
      <i
        class="pi step-icon"
        aria-hidden="true"
        [class.pi-lightbulb]="$any(group).step.type === 'agent_thinking'"
        [class.pi-comment]="$any(group).step.type === 'sub_agent_thinking'"
        [class.pi-check-circle]="$any(group).step.type === 'tool_result'"
        [class.pi-spinner]="$any(group).step.type === 'thinking'"
        [class.pi-spin]="$any(group).step.type === 'thinking'"
      ></i>
      <div class="tool-step-content">
        <!-- Every row in the panel now says what it is. Without a label the orchestrator's
             own reasoning was the one row with no marker at all, so it read as stray text. -->
        <div class="step-row-head">
          @if ($any(group).step.type === 'sub_agent_thinking') {
            <p-tag
              styleClass="step-chip step-chip-agent"
              severity="secondary"
              [rounded]="true"
              [value]="subAgentLabel($any(group).step.metadata?.['sub_agent'])"
            />
          } @else {
            <span class="step-row-label">{{ $any(group).step.type === 'tool_result' ? 'Result' : 'Reasoning' }}</span>
          }
        </div>
        <!-- Clamped rather than scrolled: a scroll region nested inside the chat scroller
             traps the wheel and hides content with no affordance that more exists. -->
        <span class="step-thinking-text" [class.is-clamped]="!isStepExpanded(msgId + '-think-' + $any(group).index)">{{
          $any(group).step.content
        }}</span>
        @if ($any(group).step.content.length > 220) {
          <button
            class="step-inline-toggle"
            type="button"
            [attr.aria-expanded]="isStepExpanded(msgId + '-think-' + $any(group).index)"
            (click)="toggleStep(msgId + '-think-' + $any(group).index)"
          >
            {{ isStepExpanded(msgId + '-think-' + $any(group).index) ? 'Show less' : 'Show more' }}
          </button>
        }
      </div>
    </div>
  } @else {
    <!-- tool_call accordion with nested tool_results -->
    <div class="tool-step tool_call accordion-step" [class.sub-agent-step]="isSubAgentCall($any(group).call)">
      <button
        class="step-accordion-header"
        type="button"
        [attr.aria-expanded]="isStepExpanded(msgId + '-call-' + $any(group).callIndex)"
        [attr.aria-controls]="msgId + '-callbody-' + $any(group).callIndex"
        (click)="toggleStep(msgId + '-call-' + $any(group).callIndex)"
      >
        <i
          class="pi step-icon"
          aria-hidden="true"
          [class.pi-wrench]="!isSubAgentCall($any(group).call)"
          [class.pi-sitemap]="isSubAgentCall($any(group).call)"
        ></i>
        <div class="step-accordion-summary">
          <span class="step-tool-name">
            {{ $any(group).call.metadata?.['tool_name'] || $any(group).call.content }}
          </span>
          @if (isSubAgentCall($any(group).call)) {
            <p-tag styleClass="step-chip step-chip-delegated" severity="info" [rounded]="true" value="delegated" />
          }
          @if ($any(group).results.length === 0) {
            <span class="step-status running"><span class="live-dot" aria-hidden="true"></span>running</span>
          } @else {
            <span class="step-result-count"
              >{{ $any(group).results.length }} result{{ $any(group).results.length !== 1 ? 's' : '' }}</span
            >
          }
        </div>
        <i
          class="pi step-accordion-chevron"
          aria-hidden="true"
          [class.pi-chevron-down]="!isStepExpanded(msgId + '-call-' + $any(group).callIndex)"
          [class.pi-chevron-up]="isStepExpanded(msgId + '-call-' + $any(group).callIndex)"
        ></i>
      </button>

      @if (isStepExpanded(msgId + '-call-' + $any(group).callIndex)) {
        <div class="step-accordion-body" [attr.id]="msgId + '-callbody-' + $any(group).callIndex">
          @if ($any(group).call.metadata?.['tool_input']) {
            <div class="step-section">
              <div class="step-section-head">
                <span class="step-section-label">Request</span>
                @if (jsonTree($any(group).call.metadata?.['tool_input']); as inputTree) {
                  <button
                    class="step-view-toggle"
                    type="button"
                    [attr.aria-pressed]="isStepExpanded(msgId + '-raw-' + $any(group).callIndex)"
                    (click)="toggleStep(msgId + '-raw-' + $any(group).callIndex)"
                  >
                    {{ isStepExpanded(msgId + '-raw-' + $any(group).callIndex) ? 'Tree' : 'Raw' }}
                  </button>
                }
              </div>
              @if (jsonTree($any(group).call.metadata?.['tool_input']); as inputTree) {
                @if (isStepExpanded(msgId + '-raw-' + $any(group).callIndex)) {
                  <pre class="tool-step-input tool-step-json">{{
                    prettyJson($any(group).call.metadata?.['tool_input'])
                  }}</pre>
                } @else {
                  <div class="json-tree">
                    <ng-container
                      *ngTemplateOutlet="
                        jsonNode;
                        context: { $implicit: inputTree, keyBase: msgId + '-jin-' + $any(group).callIndex }
                      "
                    ></ng-container>
                  </div>
                }
              } @else {
                <div class="tool-step-input">{{ $any(group).call.metadata?.['tool_input'] }}</div>
              }
            </div>
          }

          @if ($any(group).results.length > 0) {
            <div class="step-section">
              <div class="step-section-head">
                <span class="step-section-label">Response</span>
                <!-- Rendered is the readable view; raw is the one you need when the question
                     is what the tool actually returned — the literal markdown, or a payload
                     the renderer decided was prose. -->
                <button
                  class="step-view-toggle"
                  type="button"
                  [attr.aria-pressed]="isStepExpanded(msgId + '-rawres-' + $any(group).callIndex)"
                  (click)="toggleStep(msgId + '-rawres-' + $any(group).callIndex)"
                >
                  {{ isStepExpanded(msgId + '-rawres-' + $any(group).callIndex) ? 'Rendered' : 'Raw' }}
                </button>
              </div>
              @if (isStepExpanded(msgId + '-rawres-' + $any(group).callIndex)) {
                @for (result of $any(group).results; track $index) {
                  <pre class="tool-step-input step-raw">{{ result.content }}</pre>
                }
              } @else {
                @for (result of $any(group).results; track $index; let ri = $index) {
                  @if (result.metadata?.['score'] !== undefined || $any(result.metadata?.['chunk_keys'])?.length > 0) {
                    <div class="tool-result-item">
                      <button
                        class="result-header"
                        type="button"
                        [attr.aria-expanded]="isStepExpanded(msgId + '-result-' + $any(group).callIndex + '-' + ri)"
                        [attr.aria-controls]="msgId + '-resultbody-' + $any(group).callIndex + '-' + ri"
                        (click)="
                          toggleStep(msgId + '-result-' + $any(group).callIndex + '-' + ri); $event.stopPropagation()
                        "
                      >
                        <i class="pi pi-check-circle step-icon" aria-hidden="true"></i>
                        <div class="step-accordion-summary">
                          @if (result.metadata?.['score'] !== undefined) {
                            <p-tag
                              styleClass="step-chip step-chip-score"
                              severity="success"
                              [rounded]="true"
                              [value]="'score ' + $any(result.metadata?.['score']).toFixed(3)"
                            />
                          }
                          @for (key of $any(result.metadata?.['chunk_keys'] ?? []); track key) {
                            <p-tag
                              styleClass="step-chip step-chip-key"
                              severity="secondary"
                              [rounded]="true"
                              [value]="key"
                            />
                          }
                        </div>
                        <i
                          class="pi step-accordion-chevron"
                          aria-hidden="true"
                          [class.pi-chevron-down]="
                            !isStepExpanded(msgId + '-result-' + $any(group).callIndex + '-' + ri)
                          "
                          [class.pi-chevron-up]="isStepExpanded(msgId + '-result-' + $any(group).callIndex + '-' + ri)"
                        ></i>
                      </button>
                      @if (isStepExpanded(msgId + '-result-' + $any(group).callIndex + '-' + ri)) {
                        <div class="result-body" [attr.id]="msgId + '-resultbody-' + $any(group).callIndex + '-' + ri">
                          @if (result.metadata?.['metadata']) {
                            <div class="result-section-label">Metadata:</div>
                            <ul class="result-metadata-list">
                              @if (result.metadata?.['metadata']?.['description']) {
                                <li>
                                  <span class="metadata-label">Description:</span>
                                  {{ result.metadata?.['metadata']?.['description'] }}
                                </li>
                              }
                              @if (result.metadata?.['metadata']?.['category']) {
                                <li>
                                  <span class="metadata-label">Category:</span>
                                  {{ result.metadata?.['metadata']?.['category'] }}
                                </li>
                              }
                              @if (result.metadata?.['metadata']?.['year_of_publication']) {
                                <li>
                                  <span class="metadata-label">Year:</span>
                                  {{ result.metadata?.['metadata']?.['year_of_publication'] }}
                                </li>
                              }
                              @if (result.metadata?.['metadata']?.['link']) {
                                <li>
                                  <span class="metadata-label">Link:</span>
                                  <a [href]="result.metadata?.['metadata']?.['link']" target="_blank">{{
                                    result.metadata?.['metadata']?.['link']
                                  }}</a>
                                </li>
                              }
                              @if ($any(result.metadata?.['metadata']?.['keywords'])?.length > 0) {
                                <li>
                                  <span class="metadata-label">Keywords:</span>
                                  {{ $any(result.metadata?.['metadata']?.['keywords']).join(', ') }}
                                </li>
                              }
                            </ul>
                          }
                          <div class="result-section-label">Chunk text:</div>
                          <div class="tool-step-content">{{ result.content }}</div>
                        </div>
                      }
                    </div>
                  } @else {
                    <!-- JSON payloads get indented; everything else is markdown far more often
                   than not, and unrendered it becomes a wall of ## and **. Clamped either
                   way, so one verbose result cannot bury the rest. -->
                    <div class="tool-result-item plain-result">
                      @if (jsonTree(result.content); as resultTree) {
                        <div
                          class="plain-result-body json-tree"
                          besharpOverflows
                          [class.is-clamped]="!isStepExpanded(msgId + '-plain-' + $any(group).callIndex + '-' + ri)"
                          (besharpOverflows)="
                            setResultOverflow(msgId + '-plain-' + $any(group).callIndex + '-' + ri, $event)
                          "
                        >
                          <ng-container
                            *ngTemplateOutlet="
                              jsonNode;
                              context: {
                                $implicit: resultTree,
                                keyBase: msgId + '-jres-' + $any(group).callIndex + '-' + ri,
                              }
                            "
                          ></ng-container>
                        </div>
                      } @else {
                        <div
                          class="plain-result-body"
                          besharpOverflows
                          [class.is-clamped]="!isStepExpanded(msgId + '-plain-' + $any(group).callIndex + '-' + ri)"
                          (besharpOverflows)="
                            setResultOverflow(msgId + '-plain-' + $any(group).callIndex + '-' + ri, $event)
                          "
                          [innerHTML]="result.content | markdown"
                        ></div>
                      }
                      @if (showsResultToggle(msgId + '-plain-' + $any(group).callIndex + '-' + ri)) {
                        <button
                          class="step-expand"
                          type="button"
                          [class.is-open]="isStepExpanded(msgId + '-plain-' + $any(group).callIndex + '-' + ri)"
                          [attr.aria-expanded]="isStepExpanded(msgId + '-plain-' + $any(group).callIndex + '-' + ri)"
                          (click)="toggleStep(msgId + '-plain-' + $any(group).callIndex + '-' + ri)"
                        >
                          <span>{{
                            isStepExpanded(msgId + '-plain-' + $any(group).callIndex + '-' + ri)
                              ? 'Show less'
                              : 'Show more'
                          }}</span>
                          <i
                            class="pi"
                            aria-hidden="true"
                            [class.pi-chevron-down]="
                              !isStepExpanded(msgId + '-plain-' + $any(group).callIndex + '-' + ri)
                            "
                            [class.pi-chevron-up]="isStepExpanded(msgId + '-plain-' + $any(group).callIndex + '-' + ri)"
                          ></i>
                        </button>
                      }
                    </div>
                  }
                }
              }
            </div>
          }

          @if (visibleChildren(group).length > 0) {
            <div class="step-section">
              <div class="step-section-head"><span class="step-section-label">Delegated work</span></div>
              <div class="sub-agent-children">
                @for (child of visibleChildren(group); track $index) {
                  <ng-container
                    *ngTemplateOutlet="stepGroup; context: { $implicit: child, msgId: msgId }"
                  ></ng-container>
                }
              </div>
            </div>
          }
        </div>
      }
    </div>
  }
</ng-template>
